import React, { useState, useEffect, lazy, Suspense, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  Icon,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Select,
  Button,
  Flex,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import axios from "axios";
import {
  FaChalkboard,
  FaBuilding,
  FaLayerGroup,
  FaUserMinus,
  FaUserPlus,
} from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";

// Lazy load the AddSectionModal component
// const AddSectionModal = lazy(() => import('./S_modal'));

const C_Modal = ({ isOpen, onClose, refreshClassList }) => {
  const {
    isOpen: isSectionModalOpen,
    onOpen: onSectionModalOpen,
    onClose: onSectionModalClose,
  } = useDisclosure();

  // State to hold form data
  const [formData, setFormData] = useState({
    name: "",
    division: "",
    section: "",
    minStudents: "",
    maxStudents: "",
  });

  // State to hold division and section options
  const [divisions, setDivisions] = useState([]);
  const [sections, setSections] = useState([]);
  const [error, setError] = useState("");
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/admin/class/class_master.php`)
      .then((response) => {
        setClasses(response.data.data || []);
      })
      .catch((error) => console.error("Error fetching classes:", error));
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      const jwt = sessionStorage.getItem("jwt"); // Get JWT from session storage

      if (!jwt) {
        console.error("JWT is missing.");
        return;
      }

      try {
        const response = await axios.get(
          `${BASE_URL}/admin/class/division.php`,
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        setDivisions(response.data.data || []);
      } catch (error) {
        console.error("Error fetching divisions:", error);
        setError("Error fetching divisions");
      }
    };

    const fetchSections = async () => {
      const jwt = sessionStorage.getItem("jwt"); // Get JWT from session storage

      if (!jwt) {
        console.error("JWT is missing.");
        return;
      }

      try {
        const response = await axios.get(
          `${BASE_URL}/admin/class/section.php`,
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        setSections(response.data.data || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        setError("Error fetching sections");
      }
    };

    if (isOpen) {
      fetchDivisions();
      fetchSections();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const jwt = sessionStorage.getItem("jwt"); // Get JWT from session storage

    if (!jwt) {
      console.error("JWT is missing.");
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/admin/class/create_class.php`,
        {
          class: formData.name,
          division: formData.division,
          section: formData.section,
          min_students: formData.minStudents,
          max_students: formData.maxStudents,
          school_id: sessionStorage.getItem("school_id"), // School ID
        },
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      console.log("====================================");
      console.log(response);
      console.log("====================================");
      if (response.data.success) {
        console.log("Class added successfully:", response.data);
        onClose(); // Close the modal after successful submission
        refreshClassList(); // Refresh the class list
      } else {
        console.error("Error adding class:", response.data.message);
      }
    } catch (error) {
      console.error("Error adding class:", error);
      setError("Error adding class");
    }
  };

  return (
    <>
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add Class
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl id="name" mb={4}>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={FaChalkboard} boxSize={5} color="teal.500" mr={2} />
                  Class Name
                </FormLabel>
                <Select
                  name="name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Class"
                  value={formData.name}
                  onChange={handleChange}
                  flex="1"
                >
                  {classes.length > 0 &&
                    classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.class}
                      </option>
                    ))}
                </Select>
              </FormControl>
              <FormControl id="division" mb={4}>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={FaBuilding} boxSize={5} color="teal.500" mr={2} />
                  Select Division
                </FormLabel>{" "}
                <Flex align="center">
                  <Select
                    name="division"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    placeholder="Select Division"
                    value={formData.division}
                    onChange={handleChange}
                    flex="1"
                  >
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.division}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>
              <FormControl id="section" mb={4}>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={FaLayerGroup} boxSize={5} color="teal.500" mr={2} />
                  Select Section
                </FormLabel>{" "}
                <Flex align="center">
                  <Select
                    name="section"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    placeholder="Select Section"
                    value={formData.section}
                    onChange={handleChange}
                    flex="1"
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.section}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>
              <FormControl id="minStudents" mb={4}>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={FaUserMinus} boxSize={5} color="teal.500" mr={2} />
                  Min No of Students
                </FormLabel>{" "}
                <Input
                  type="number"
                  name="minStudents"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.minStudents}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl id="maxStudents" mb={4}>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={FaUserPlus} boxSize={5} color="teal.500" mr={2} />
                  Max No of Students
                </FormLabel>{" "}
                <Input
                  type="number"
                  name="maxStudents"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.maxStudents}
                  onChange={handleChange}
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleSubmit}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Close
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Second Modal */}
      {/* <Suspense fallback={<div>Loading...</div>}>
                <AddSectionModal isOpen={isSectionModalOpen} onClose={onSectionModalClose} />
            </Suspense> */}
    </>
  );
};

export default C_Modal;
